import { type FC } from 'react';
import { Alert, Form, Input, Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Text, Space, Button, Checkbox, InputText } from '@g2u/ui-kit';
import cx from 'classnames';
import Big from 'big.js';

import { Condition, JsonData, JsonDataItem, PaymentMethod, ServiceInvoice, type WithOptionalDataTest } from 'types';
import { PaymentSteps } from '../../hooks';
import styles from './invoice.module.scss';
import { useJob } from 'helpers';

type Props = {
  jobId?: string;
  step: PaymentSteps;
  hasDebt: boolean;
  invoices?: ServiceInvoice[];
  paymentMethods?: JsonData;
  paymentMethod?: PaymentMethod;
  onRequestPayment: () => void;
  onProcessPayment: () => void;
  isLoading?: boolean;
  incompleteDetailsMessage?: string;
} & WithOptionalDataTest;

export const Invoice: FC<Props> = (props) => {
  const {
    jobId,
    hasDebt,
    invoices,
    paymentMethods,
    paymentMethod,
    isLoading,
    step,
    onRequestPayment,
    onProcessPayment,
    incompleteDetailsMessage,
  } = props;
  const { job } = useJob(jobId);
  const navigate = useNavigate();
  const form = Form.useFormInstance();
  Form.useWatch(['useCardOnFile'], { form });
  const invoiceTotal = job?.support_legacy
    ? Big(job?.job?.job_total || 0).toFixed(2)
    : Big(job?.service_total.replaceAll(',', '') || 0).toFixed(2);
  const selectedExistingCard = Form.useWatch('useCardOnFile', form) === Condition.Yes;
  const shouldProcessPayment = paymentMethod === PaymentMethod.CreditCard && selectedExistingCard;
  const shouldRequestPayment = paymentMethod === PaymentMethod.CreditCard && !selectedExistingCard;
  const requestPaymentBtnText = shouldRequestPayment
    ? 'Request Payment'
    : shouldProcessPayment
      ? 'Process Payment'
      : 'Save Receipt';

  const goToServiceDetails = () => navigate(`/job/edit/${job?.ID}/service-details`);
  const handleProcessPayment = () => {
    shouldRequestPayment ? onRequestPayment() : onProcessPayment();
  };

  return (
    <>
      <Text.$ strong size={Text.Size.LG} className={cx(styles.invoice__title)}>
        Invoice
      </Text.$>
      {isLoading ? (
        <Skeleton />
      ) : (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} align={Space.Align.Start} stretch>
          {invoices?.length ? (
            <>
              {invoices.map((invoice) => {
                const isMethodSupported = paymentMethods?.some((method) => method.ID === invoice.payment_method);
                const isActionRequired = !(invoice.payment_received === invoice.total || Number(invoice.total) < 0);
                return (
                  <div key={invoice.ID} className={styles.invoice__list}>
                    <Space.$ size={Space.Size.Small} align={Space.Align.End}>
                      <InputText.$
                        name={`invoice-id-${invoice.ID}`}
                        label="Invoice ID:"
                        initialValue={invoice.ID.toString()}
                        disabled
                        width={115}
                      />
                      <InputText.$
                        name={`invoice-date-${invoice.ID}`}
                        label="Invoice Date:"
                        initialValue={invoice.date}
                        disabled
                        width={115}
                      />
                      {isMethodSupported && (
                        <InputText.$
                          name={`invoice-method-${invoice.ID}`}
                          label="Payment Method:"
                          initialValue={
                            paymentMethods?.find((method: JsonDataItem) => method.ID === invoice.payment_method)?.name
                          }
                          disabled
                          width={200}
                        />
                      )}
                      <InputText.$
                        name={`invoice-total-${invoice.ID}`}
                        label="Invoice Total:"
                        initialValue={invoice.total}
                        disabled
                        width={115}
                        className={styles.invoice__total}
                      />
                      {/* TODO: Implement when API is ready */}
                      <Button.$ dataTestId="print-invoice-btn" onClick={() => null} disabled>
                        Print
                      </Button.$>
                    </Space.$>

                    {hasDebt && step === PaymentSteps.ProcessPayment && isActionRequired && (
                      <Button.$
                        dataTestId="request-extra-payment"
                        type={Button.Type.Primary}
                        className={styles.invoice__paymentBtn}
                        onClick={handleProcessPayment}
                      >
                        {requestPaymentBtnText}
                      </Button.$>
                    )}
                  </div>
                );
              })}
            </>
          ) : null}
          {step === PaymentSteps.GenerateInvoice && (
            <>
              <div>
                <br />
                <Input value={`$${invoiceTotal}`} style={{ width: 115 }} disabled />{' '}
                <Button.$ dataTestId="edit-invoice-btn" onClick={goToServiceDetails}>
                  Edit
                </Button.$>
              </div>
              {incompleteDetailsMessage && (
                <Alert
                  message={incompleteDetailsMessage}
                  type="warning"
                  showIcon
                  style={{ borderRadius: 4 }}
                  action={
                    <Button.$ size={Button.Size.Small} onClick={goToServiceDetails} dataTestId="missed-details-action">
                      Edit service details
                    </Button.$>
                  }
                />
              )}
              <Checkbox.$
                name="isDetailsConfirmed"
                label={<div style={{ textWrap: 'nowrap' }}>I confirm that all invoice details are correct.</div>}
              />
            </>
          )}
        </Space.$>
      )}
    </>
  );
};
